import m from "mithril";
import i18n from "../../shared/i18n/i18n";
import ModalManager from "../../shared/modal";
import FormUtilities from "../../shared/utilities/form_utilities";
import DropdownField from "../../shared/components/component_dropdown_field";
import { buildDependencyState, fieldHasOptions } from "../../shared/utilities/assessment_dependencies";

function calculateRows(value) {
    const maxLength = 65;
    const lines = value?.split("\n") || [];
    return Math.max(1, lines.reduce((count, line) => count + Math.ceil(line.length / maxLength), 0));
}

function requiredFieldIsEmpty(field) {
    if (!field.isRequired) return false;
    return fieldHasOptions(field.type)
        ? !field.options.some(o => o.selected)
        : !field.value?.trim();
}

function fieldIsRequiredMessage(field) {
    const messages = {
        email: "email_not_valid",
        number: "number_not_valid",
        date: "date_not_valid",
        checkbox: "checkbox_not_valid",
        radio: "radio_button_not_valid",
        default: "field_mandatory"
    };
    return i18n.t(messages[field.type] || messages.default);
}

function getTextErrorElem(message) {
    return m(".text-error.padding", { style: "font-size: 0.75rem;" }, message);
}

export default {
    state: {
        modalId: null,
        originalUnit: null,
        unitEnrolmentId: null,
        enrolmentId: null,
        unitId: null,
        formUnit: null,
        loadFromAdmin: false,
        exitCallback: () => {},
        loading: false,
        invalidEmailFieldIds: [],
        fieldValidationErrors: {},
        displayFieldRequiredErrors: false
    },

    setModalId(id) {
        this.state.modalId = id;
    },

    oninit(vnode) {
        const { enrolmentId, unitId, unit, callback, loadFromAdmin, unitEnrolmentId, record } = vnode.attrs;
        Object.assign(this.state, {
            originalUnit: unit,
            unitEnrolmentId: unitEnrolmentId || unit?.enrolment?.id,
            enrolmentId,
            unitId,
            exitCallback: callback,
            loadFromAdmin,
            loading: false,
            invalidEmailFieldIds: [],
            fieldValidationErrors: {},
            displayFieldRequiredErrors: false,
            formUnit: record
        });
    },

    view(vnode) {
        const state = this.state;

        if (state.loading || !state.formUnit) {
            return m(".text-gray", [i18n.t("loading"), m(".loading-dots")]);
        }

        const renderField = (field) => {
            const setValue = (value) => {
                state.dirty = true;
                field.value = value;
            };

            const baseProps = {
                value: field.value || "",
                placeholder: field.info,
                oninput: e => setValue(e.target.value)
            };

            if (["textfield", "number", "date"].includes(field.type)) {
                const type = field.type === "textfield" ? "text" : field.type;
                return m(`input[type=${type}].form-input`, baseProps);
            }

            if (field.type === "textbox") {
                return m("textarea.form-input", { ...baseProps, rows: calculateRows(field.value || "") });
            }

            if (field.type === "email") {
                return m("input[type=email].form-input", {
                    ...baseProps,
                    placeholder: i18n.t("enter_email"),
                    oninput: e => {
                        setValue(e.target.value);
                        const isValid = FormUtilities.validateEmail(field.value, !field.isRequired);
                        const index = state.invalidEmailFieldIds.indexOf(field.id);
                        if (!isValid && index === -1) {
                            state.invalidEmailFieldIds.push(field.id);
                        } else if (isValid && index !== -1) {
                            state.invalidEmailFieldIds.splice(index, 1);
                        }
                    }
                });
            }

            if (["checkbox", "radio"].includes(field.type)) {
                return m(`.form-input.${field.type}-group`,
                    field.options.map(option =>
                        m("label", [
                            m(`input[type=${field.type}]`, {
                                name: field.id,
                                checked: !!option.selected,
                                onchange: () => {
                                    state.dirty = true;
                                    if (field.type === "radio") {
                                        field.options.forEach(o => o.selected = false);
                                    }
                                    option.selected = !option.selected;
                                }
                            }),
                            option.value
                        ])
                    )
                );
            }

            if (field.type === "dropdownsingle" || field.type === "dropdownmulti") {
                return m(DropdownField, { field: field, readOnly: true });
            }

            return "";
        };

        const validateField = (field) => {
            if (requiredFieldIsEmpty(field)) {
                const msg = fieldIsRequiredMessage(field);
                state.fieldValidationErrors[field.id] = msg;
                return state.displayFieldRequiredErrors ? getTextErrorElem(msg) : null;
            }

            if (field.type === "email" && state.invalidEmailFieldIds.includes(field.id)) {
                const msg = i18n.t("email_must_be_valid");
                state.fieldValidationErrors[field.id] = msg;
                return getTextErrorElem(msg);
            }

            delete state.fieldValidationErrors[field.id];
            return null;
        };

        const dependencyState = buildDependencyState(state.formUnit.sections);

        const formSections = state.formUnit.sections.map(section =>
            dependencyState.activeSectionIds.has(section.id) &&
            m(".form-section.mb-2.full-width", [
                m(".tile.tile-centered.m-0",
                    m(".tile-content", [
                        m(".form-label", section.name),
                        section.info && m(".form-subtitle.text-italic.mb-3", section.info)
                    ])
                ),
                m(".form.form-section-fields.full-width",
                    section.fields.map(field =>
                        dependencyState.activeFieldIds.has(field.id) &&
                        m(".form-section", {
                            class: [field.isRequired && "required", state.fieldValidationErrors[field.id] && "has-error"].filter(Boolean).join(" ")
                        }, [
                            m(".form-label", field.name),
                            renderField(field),
                            validateField(field)
                        ])
                    )
                )
            ])
        );

        const formHasErrors = Object.keys(state.fieldValidationErrors).length > 0;

        return [
            m(".form-sections.form.full-width", { class:  "disable-fields" }, formSections),
            formHasErrors && state.displayFieldRequiredErrors &&
            m(".text-error.padding", { style: "font-size: 0.75rem;" }, i18n.t("complete_required_fields"))
        ];
    },

    cancel() {
        this.state.exitCallback();
        ModalManager.close(this.state.modalId);
    }
};
